/**
 * advisorAccessApi — Client-Wrapper für die Advisor-Zugriffs-/Audit-Callables (B5,
 * architecture-stb-onboarding-consent.md §7).
 *
 * Dünne httpsCallable-Bindings (Muster {@link ./advisorInviteApi}). Alle harten
 * Sicherheits-/Compliance-Prüfungen liegen server-seitig; diese Helfer marshallen
 * nur Input/Output und normalisieren Fehler für die UI.
 *
 * Beide Callables sind server-seitig AUTH-ONLY (kein Feature-/Beta-Gate): ein
 * Widerruf muss immer möglich sein (§7.5), und die Session-Grain-Protokollierung
 * (§7.3) läuft additiv unabhängig vom Enforcement-Flag.
 *
 * @module api/advisorAccessApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/** Erfolgs-Antwort von {@link revokeAdvisorAccess}. */
export interface RevokeAdvisorAccessResult {
  status: 'ok';
  /** `true`, wenn die Kante bereits widerrufen war (idempotenter No-Op). */
  alreadyRevoked: boolean;
}

/** Erfolgs-Antwort von {@link grantAdvisorConsent}. */
export interface GrantAdvisorConsentResult {
  status: 'ok';
  /** `true`, wenn die Kante bereits eingewilligt hatte (Marker-Refresh). */
  alreadyGranted: boolean;
}

/** Eingabe von {@link grantAdvisorConsent}. */
export interface GrantAdvisorConsentInput {
  /** uid der Kanzlei, deren aktive Kante die Einwilligung erhält. */
  advisorUid: string;
  /** Im §3-Dialog angezeigter Kanzlei-Name (wird server-seitig denormalisiert). */
  kanzleiName?: string | null;
}

/**
 * Widerruft den Lesezugriff einer Kanzlei auf den EIGENEN Tenant des Aufrufers
 * (§7.5). Der Aufrufer ist der Mandant/Owner; `advisorUid` benennt die Kanzlei,
 * deren aktive `advisor`-Membership beendet wird. Server-seitig auth-only,
 * idempotent und field-gebunden (nur die eigene Scope-Kante widerrufbar).
 *
 * @param advisorUid uid der Kanzlei, deren Zugriff widerrufen wird.
 * @throws Error mit lesbarer Meldung bei jeder server-seitigen Ablehnung.
 */
export async function revokeAdvisorAccess(
  advisorUid: string,
): Promise<RevokeAdvisorAccessResult> {
  const callable = httpsCallable<{ advisorUid: string }, RevokeAdvisorAccessResult>(
    functions,
    'revokeAdvisorAccess',
  );
  try {
    const res = await callable({ advisorUid });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Erteilt die §62a-StBerG-Einwilligung des Mandanten für eine BESTEHENDE aktive
 * Kanzlei-Kante auf dem EIGENEN Tenant (F1/B2 Self-Service — kein Re-Invite, kein
 * Admin). Server-seitig auth-only, owner-only, field-gebunden und nur für aktive
 * Kanten; schreibt server-only den Consent-Marker + `advisor_consent_log`-Eintrag.
 * Idempotent: eine bereits eingewilligte Kante liefert `alreadyGranted:true` ohne
 * Fehler (Marker-Refresh).
 *
 * @param input `{ advisorUid, kanzleiName }` — Name aus dem §3-Dialog (Denormalisierung).
 * @throws Error mit lesbarer Meldung bei jeder server-seitigen Ablehnung.
 */
export async function grantAdvisorConsent(
  input: GrantAdvisorConsentInput,
): Promise<GrantAdvisorConsentResult> {
  const callable = httpsCallable<GrantAdvisorConsentInput, GrantAdvisorConsentResult>(
    functions,
    'grantAdvisorConsent',
  );
  try {
    const res = await callable({
      advisorUid: input.advisorUid,
      kanzleiName: input.kanzleiName ?? null,
    });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Protokolliert EIN `context_enter`-Audit-Ereignis, sobald ein Advisor in den
 * Kontext eines Mandanten wechselt (§7.3 Session-Grain-Abdeckung der Client-
 * Direct-Reads). Fire-and-forget-tauglich: der Server wirft NUR bei einer echten
 * Compliance-Ablehnung; Owner-Selbstzugriff ist ein No-Op (kein Audit).
 *
 * @param mandantUid uid des Mandanten-Tenants, in den gewechselt wird.
 * @throws Error mit lesbarer Meldung nur bei echter Compliance-Ablehnung.
 */
export async function logAdvisorContextEnter(
  mandantUid: string,
): Promise<{ status: 'ok' }> {
  const callable = httpsCallable<{ mandantUid: string }, { status: 'ok' }>(
    functions,
    'logAdvisorContextEnter',
  );
  try {
    const res = await callable({ mandantUid });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server message
 * and exposing the coarse function error code (e.g. `permission-denied`).
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
